import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getExtAdminMerchants } from "../../../redux/actions/profile";
import MerchantDetailPanel from "../../shared/MerchantDetailPanel";
import MerchantFilterBar from "../../shared/MerchantFilterBar";
import {
  DEFAULT_MERCHANT_FILTERS,
  applyMerchantFilters,
} from "../../../utility/merchantFilters";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

export default function Merchants() {
  const dispatch = useDispatch();
  const merchants = useSelector((s) => s.profile.extAdminMerchants);
  const totalCommission = useSelector((s) => s.profile.extAdminCommission);
  const [filters, setFilters] = useState(DEFAULT_MERCHANT_FILTERS);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    dispatch(getExtAdminMerchants());
  }, [dispatch]);

  const allMerchants = useMemo(() => merchants || [], [merchants]);

  const directCount = allMerchants.filter(
    (m) => m.referredBy?.type === "admin",
  ).length;
  const agentCount = allMerchants.filter(
    (m) => m.referredBy?.type === "agent",
  ).length;

  const filtered = useMemo(
    () => applyMerchantFilters(allMerchants, filters),
    [allMerchants, filters],
  );

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h2 style={styles.heading}>Merchant Referrals</h2>
          <p style={styles.subText}>
            All merchants referred by you directly or through your agents.
          </p>
        </div>
        <div style={styles.commissionBadge}>
          <i
            className='bi bi-cash-coin'
            style={{ color: PRIMARY, marginRight: 6 }}
          />
          Total Commission:{" "}
          <strong style={{ color: PRIMARY, marginLeft: 6 }}>
            ₦{(totalCommission || 0).toLocaleString()}
          </strong>
        </div>
      </div>

      {/* summary chips */}
      <div style={styles.chips}>
        {[
          ["all", "All", allMerchants.length],
          ["admin", "Direct (you)", directCount],
          ["agent", "Via agents", agentCount],
        ].map(([key, label, count]) => (
          <button
            key={key}
            style={chipStyle(filters.referrerType === key)}
            onClick={() => setFilters((f) => ({ ...f, referrerType: key }))}
          >
            {label} &nbsp;<span style={styles.count}>{count}</span>
          </button>
        ))}
      </div>

      <MerchantFilterBar
        merchants={allMerchants}
        filters={filters}
        onChange={setFilters}
        searchPlaceholder='Search store, email, agent name or ref code…'
        resultCount={filtered.length}
      />

      {filtered.length === 0 ? (
        <div style={styles.empty}>
          <i className='bi bi-shop' style={{ fontSize: 40, color: "#ccc" }} />
          <p style={{ marginTop: 12, color: "#888" }}>No merchants found.</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {[
                  "Store Name",
                  "Email",
                  "Referred By",
                  "Plan",
                  "Subscription",
                  "Last Login",
                  "Joined",
                  "",
                ].map((h) => (
                  <th key={h} style={styles.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const storeName = m.storeInfo?.storeName || m.email;
                const isActive =
                  m.subscriptionEnd && new Date().getTime() < m.subscriptionEnd;
                const ref = m.referredBy;
                return (
                  <tr key={m._id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600, color: BG, fontSize: 13 }}>
                        {storeName}
                      </div>
                      {m.storeInfo?.storeName && (
                        <div style={{ fontSize: 11, color: "#999" }}>
                          {m.email}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{m.email}</td>
                    <td style={styles.td}>
                      {ref ? (
                        <div>
                          <span
                            style={{
                              background:
                                ref.type === "admin"
                                  ? "rgba(167,204,72,0.12)"
                                  : "rgba(79,176,198,0.12)",
                              color:
                                ref.type === "admin" ? "#5a8a00" : "#1a7a90",
                              borderRadius: 20,
                              padding: "2px 10px",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {ref.type === "admin" ? "Direct" : "Agent"}
                          </span>
                          <div
                            style={{
                              fontSize: 11,
                              color: "#888",
                              marginTop: 3,
                            }}
                          >
                            {ref.name}
                            {ref.code && (
                              <span
                                style={{
                                  marginLeft: 4,
                                  fontFamily: "monospace",
                                  color: "#bbb",
                                }}
                              >
                                ({ref.code})
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: "#ccc" }}>—</span>
                      )}
                    </td>
                    <td style={styles.td}>{m.plan || "—"}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          background: isActive
                            ? "rgba(167,204,72,0.15)"
                            : "#f5f5f5",
                          color: isActive ? "#5a8a00" : "#888",
                          borderRadius: 20,
                          padding: "2px 12px",
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: "#888" }}>
                      {m.lastLogin
                        ? new Date(m.lastLogin).toLocaleString()
                        : "Never"}
                    </td>
                    <td style={styles.td}>
                      {m.createdAt
                        ? new Date(m.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td style={styles.td}>
                      <button style={styles.viewBtn} onClick={() => setSelected(m)}>
                        <i className="bi bi-eye" /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <MerchantDetailPanel
          merchant={selected}
          onClose={() => setSelected(null)}
          canViewSensitive={true}
        />
      )}
    </div>
  );
}

const chipStyle = (active) => ({
  border: `1.5px solid ${active ? "#a7cc48" : "#e0e0e0"}`,
  background: active ? "rgba(167,204,72,0.12)" : "#fff",
  color: active ? "#5a8a00" : "#666",
  borderRadius: 20,
  padding: "5px 14px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  marginRight: 8,
  marginBottom: 12,
});

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
    flexWrap: "wrap",
    gap: 12,
  },
  heading: { fontSize: 22, fontWeight: 700, color: BG, margin: 0 },
  subText: { color: "#888", fontSize: 13, marginTop: 4 },
  commissionBadge: {
    background: "#fff",
    border: "1.5px solid #a7cc48",
    borderRadius: 6,
    padding: "7px 16px",
    fontSize: 14,
    color: "#444",
    display: "flex",
    alignItems: "center",
  },
  chips: { display: "flex", flexWrap: "wrap", marginBottom: 4 },
  count: {
    background: "#f0f0f0",
    borderRadius: 10,
    padding: "1px 7px",
    fontSize: 11,
    fontWeight: 700,
    color: "#555",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: "#fff",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  th: {
    background: BG,
    color: "#fff",
    padding: "12px 14px",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid #f0f0f0" },
  td: {
    padding: "12px 14px",
    fontSize: 13,
    color: "#444",
    verticalAlign: "middle",
  },
  empty: { textAlign: "center", padding: 60, background: "#fff", borderRadius: 10 },
  viewBtn: { background: BG, color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 },
};
